import { Group, Vector3 } from 'three';

// Named pivots are shared by the Blender mesh and the lightweight runtime.
// Small facial movement remains independent of work / swim / voyage poses.
// The authored 1.895-unit figure must fit the island's 1.60-unit cottage door.
export const AVATAR_WORLD_SCALE = .78;

// The authored limbs have separate meshes but no knee/elbow pivots. Reparent
// only their lower sections, preserving every mesh's neutral transform.
export function prepareAvatarSeatedRig(actor) {
  if (!actor?.userData.limbs) return null;
  if (actor.userData.seatedRig) return actor.userData.seatedRig;
  const limbs = actor.userData.limbs, knees = [], elbows = [];
  const neutral = Object.values(limbs).map(limb => [limb, limb.rotation.clone()]);
  function joint(parent, y, name, lower) {
    const children = parent.children.filter(lower);
    const pivot = new Group(); pivot.name = name; pivot.position.y = y;
    parent.add(pivot);
    for (const child of children) {
      child.position.sub(pivot.position);
      pivot.add(child);
    }
    return pivot;
  }
  for (const side of ['left', 'right']) {
    const leg = limbs[`${side}Leg`], arm = limbs[`${side}Arm`];
    if (!leg || !arm) return null;
    const authored = leg.name.startsWith('Avatar_Leg_');
    knees.push(joint(leg, authored ? -.35 : -.22, `Avatar_Knee_${side}`, child => authored
      ? /^Avatar_(Calf|TrouserHem|Boot|Sole)/.test(child.name) || actor.userData.fins?.includes(child)
      : child.position.y < -.22));
    elbows.push(joint(arm, authored ? -.24 : -.20, `Avatar_Elbow_${side}`, child => authored
      ? /^Avatar_(Forearm|Hand|Thumb|ToolGrip)/.test(child.name)
      : child.position.y < -.20));
  }
  actor.updateWorldMatrix(true, true);
  const hip = actor.worldToLocal(limbs.leftLeg.getWorldPosition(new Vector3())
    .add(limbs.rightLeg.getWorldPosition(new Vector3())).multiplyScalar(.5));
  return actor.userData.seatedRig = { knees, elbows, neutral, hip, seated: false };
}

export function resetAvatarSeatedJoints(actor) {
  const rig = actor?.userData.seatedRig;
  if (!rig) return;
  for (const joint of [...rig.knees, ...rig.elbows]) joint.rotation.set(0, 0, 0);
  if (rig.seated) {
    for (const [limb, rotation] of rig.neutral) limb.rotation.copy(rotation);
    rig.seated = false;
  }
}

export function poseAvatarSeated(actor, { keepArms = false } = {}) {
  const rig = prepareAvatarSeatedRig(actor);
  if (!rig) return;
  const { leftLeg, rightLeg, leftArm, rightArm } = actor.userData.limbs;
  leftLeg.rotation.set(-Math.PI / 2, 0, -.40);
  rightLeg.rotation.set(-Math.PI / 2, 0, .40);
  for (const knee of rig.knees) knee.rotation.set(Math.PI / 2, 0, 0);
  if (!keepArms) {
    leftArm.rotation.set(-.18, 0, -.04);
    rightArm.rotation.set(-.18, 0, .04);
    actor.userData.toolRoot?.rotation.set(0, 0, 0);
  }
  for (const elbow of rig.elbows) elbow.rotation.set(keepArms ? -.75 : -.72, 0, 0);
  if (actor.userData.tools?.dive) actor.userData.tools.dive.visible = false;
  for (const fin of actor.userData.fins || []) fin.visible = false;
  rig.seated = true;
}

// Bend the existing lower-arm pivots without changing the authored mesh.
// The caller resets joints before posing, preventing work poses leaking out.
export function poseAvatarWorkElbows(actor, kind, progress = 0) {
  const rig = prepareAvatarSeatedRig(actor);
  if (!rig) return;
  const beat = Math.sin(Math.max(0, Math.min(1, progress)) * Math.PI);
  const bends = {
    wateringCan: [-.20 - beat * .18, -.18 - beat * .22],
    rod: [-.24 - beat * .16, -.28 - beat * .20],
    axe: [-.18 - beat * .20, -.20 - beat * .24],
  }[kind];
  if (!bends) return;
  rig.elbows.forEach((elbow, index) => { elbow.rotation.x = bends[index]; });
}

export function animateAvatarFace(actor, time, working = false) {
  const face = actor?.userData.face;
  if (!face) return;
  const beat = ((time % 4.8) + 4.8) % 4.8;
  const blink = beat < .18 ? Math.max(.07, Math.abs(beat - .09) / .09) : 1;
  for (const eye of face.eyes) eye.scale.y = blink;
  face.head.rotation.y = Math.sin(time * .65) * (working ? .015 : .045);
  face.head.rotation.x = working ? -.035 : Math.sin(time * .85) * .018;
}
